import { randomUuid } from "@models/utils/uuid";
import { Emitter } from "@core/events/emitter";
import type { FolderDto, FolderInfo } from "@models/data/folderDto";
import {
  applyWireFolderRename,
  FOLDER_EVENT_TYPE,
  folderFromWire,
  folderToWire,
  isWireFolder,
  wireFolderId,
  type WireFolder,
} from "@models/data/folderWire";
import { Ioc } from "@core/ioc/ioc";
import type { IAuthModel } from "@models/auth/IAuthModel";
import { AUTH_MODEL } from "@models/auth/IAuthModel";
import type { ProjectInfo } from "@models/data/projectInfo";
import {
  isSharedProject,
  isUserCreatedProject,
  lastModifiedByName,
  type ProjectDto,
} from "@models/data/projectDto";
import type { IStorageModel } from "@models/storage/IStorageModel";
import { STORAGE_MODEL } from "@models/storage/IStorageModel";
import type { ISharedUsersModel } from "@models/sharing/ISharedUsersModel";
import { SHARED_USERS_MODEL } from "@models/sharing/ISharedUsersModel";
import { clearProjectThumbnails } from "@models/storage/thumbnailCache";
import { PROJECT_SERIALIZATION_V2 } from "@models/storage/projectStorageV2";
import { randomTemplateName } from "@models/utils/templateNames";
import type { IEventModel } from "@models/events/IEventModel";
import { EVENT_MODEL } from "@models/events/IEventModel";
import { getAppInstanceId } from "@models/multiuser/appInstanceId";
import {
  ADMIN_STORAGE_MODEL,
  type ExternalProjectShare,
  type HttpAdminStorageModel,
} from "@models/storage/HttpAdminStorageModel";
import {
  DEEPLINK_TYPE,
  INTERNAL_SHARE_EMAIL,
  isRosterShareLink,
  SHARED_USER_ROLE,
  sharedLinkUserRole,
  type SharedLinkDto,
} from "@models/data/sharedLinkDto";
import type { IProjectModel } from "@models/project/IProjectModel";
import { PROJECT_MODEL } from "@models/project/ProjectModel";
import {
  backfillDuplicatedMediaAsync,
  duplicateProjectOnServerAsync,
  generateNextCopyName,
  rekeyDuplicatedWorkflowAsync,
} from "./projectDuplication";
import type { IProjectListModel, ProjectDuplicationState } from "./IProjectListModel";

/**
 * `HttpProjectListModel` — production {@link IProjectListModel} backed
 * by `IStorageModel.getProjectsByCustomer`.
 *
 * Mirrors Unity's `CustomerModel.GetFoldersAndProjectsAsync` →
 * `IStorageModel.GetProjectsByCustomer` flow
 * (`shared-application-code/.../Models/CustomerModel/CustomerModel.cs`).
 * The Dynamo round trip lives in `HttpStorageModel`; this class is
 * just the orchestrator: it keeps a reactive copy of the catalog,
 * derives Unity's drafts / shared / learn category from `CreatorId`
 * + `AccountRole` + explicit shared links + `Author`, and emits `onChanged`
 * for the UI.
 *
 * Folders live here too, for the reason spelled out on `IProjectListModel`:
 * Unity fetches both in one `GetFoldersAndProjectsAsync` because a folder's
 * membership is a set of project ids and its very visibility depends on the
 * resolved projects.
 *
 * Web-port deliberate deviations vs Unity:
 *   - **External (cross-account) folders are not loaded.** Unity's
 *     `CustomerModel.LoadExternalFolders` (`CustomerModel.cs:1172-1215`) walks
 *     the accounts that shared a project with you, assumes their credentials
 *     via `IAuthModel.ActivateExternalProjectCredentials`, and reads their
 *     folder rows. The web port has no credential-assumption path — its
 *     cross-account reach is the admin GraphQL shares pipeline alone — so a
 *     project shared from another account always appears at the ROOT here.
 *     That is also what Unity falls back to when the activation fails, so the
 *     two clients agree on the failure shape; it is a missing capability, not a
 *     contract divergence. Nothing is written for those folders, so no data can
 *     drift.
 *   - External shares (`AwsAdminStorageModel.GetExternalProjectShares`)
 *     are joined in `mergeExternalSharesAsync`.
 *
 * Mutating endpoints — current state:
 *   - `renameAsync` and `deleteAsync` write straight to Dynamo via
 *     `IStorageModel.updateProjectName` / `deleteCustomerProject`,
 *     mirroring Unity's `CustomerModel.UpdateProjectNameAsync` /
 *     `DeleteProjectAsync` orchestrators (single-row writes; per
 *     `DynamoClient.cs:333` / `416`).
 *   - `createDraftAsync` writes a single project row via
 *     `IStorageModel.createCustomerProject` (mirrors Unity's
 *     `CustomerModel.CreateProjectAsync` →
 *     `IMetadataStorageModel.CreateCustomerProject`,
 *     `DynamoClient.cs:260`). Like Unity, it does **not** write a scene
 *     row here — the mandatory first scene is created + persisted on
 *     first **load** by `ProjectModel.loadProjectAsync` when the scene
 *     list comes back empty (port of `ProjectModelSlim.cs:425-426`), so
 *     every project always opens with at least one scene.
 *   - `duplicateAsync` drives the admin `projduplicate` lambda and waits for
 *     the clone — see `projectDuplication.ts`, and `duplicateAsync` below for
 *     the ordering it has to keep.
 *   - Free-tier project quota (`IsOverProjectLimit` /
 *     `k_freeUserMaxProjects = 5`) is **not** enforced here yet — the
 *     web port doesn't model paid / core-license state. Add the gate
 *     in `ProjectsView.onNewProject` once `ICustomerModel` surfaces
 *     `isPaid` / `hasCoreLicense`.
 */
export class HttpProjectListModel implements IProjectListModel {
  readonly onChanged = new Emitter<ReadonlyArray<ProjectInfo>>();
  readonly onLoadingChanged = new Emitter<boolean>();
  readonly onBeginDeleteProject = new Emitter<{ projectId: string; name: string }>();
  readonly onDeleteProjectProgress = new Emitter<{
    projectId: string;
    progress01: number;
  }>();
  readonly onDeleteProjectCompleted = new Emitter<{ projectId: string }>();
  readonly onDeleteProjectFailed = new Emitter<{ projectId: string; error: Error }>();

  readonly onFoldersChanged = new Emitter<ReadonlyArray<FolderInfo>>();

  private m_projects: ProjectInfo[] = [];
  private m_folders: FolderInfo[] = [];
  private m_isLoading = false;
  private m_lastError: Error | null = null;
  private m_inflight: AbortController | null = null;
  /**
   * The duplication in flight, or `null`. Unity keeps the same three fields on
   * `ProjectDuplicationModel` (`SourceProjectID` / `TargetProjectID` /
   * `TargetName`) and one of them, `IsProcessing`, is what refuses a second
   * duplication — the lambda is not safe to run twice at once.
   */
  private m_duplication: {
    readonly sourceId: string;
    readonly targetId: string;
    readonly placeholder: ProjectInfo;
  } | null = null;

  get projects(): ReadonlyArray<ProjectInfo> {
    return this.m_projects;
  }

  get folders(): ReadonlyArray<FolderInfo> {
    return this.m_folders;
  }

  get isLoading(): boolean {
    return this.m_isLoading;
  }

  get lastError(): Error | null {
    return this.m_lastError;
  }

  async refreshAsync(signal?: AbortSignal): Promise<ReadonlyArray<ProjectInfo>> {
    this.m_inflight?.abort();
    const controller = new AbortController();
    this.m_inflight = controller;
    const linked = signal ? linkSignals(controller.signal, signal) : controller.signal;

    this.setLoading(true);
    try {
      const auth = Ioc.resolve<IAuthModel>(AUTH_MODEL);
      // Mirrors Unity's `m_storageModel.GetProjectsByCustomer(customerID)`
      // (`CustomerModel.cs`) — `customerID` is the multi-tenant
      // `custom:accountid` claim, NOT the per-user `sub`. Categorization
      // below still uses `currentUser.userId` to compare against
      // `dto.CreatorId` for the drafts/shared split (Unity does the
      // same: `CreatorId == CurrentUser.UserID`).
      const customerId = auth.currentUser?.customerId;
      const userId = auth.currentUser?.userId ?? "";
      if (!customerId) {
        // Not signed in (or stub session). Surface an empty list so
        // the grid renders its empty state instead of crashing.
        this.m_projects = [];
        this.m_folders = [];
        this.m_lastError = null;
        this.emitChanged();
        this.emitFoldersChanged();
        return this.m_projects;
      }

      const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
      // Unity fetches projects first and folders second
      // (`CustomerModel.cs:1094`) only because its folder pass reads the
      // already-mapped project list. The web joins them after both settle, so
      // they can go out together — and a folder failure must not take the grid
      // down with it, which is why the folder leg carries its own catch
      // (Unity wraps the same call in its own try/catch, `:1092-1105`).
      const [dtos, folderRows, sharedLinks] = await Promise.all([
        storage.getProjectsByCustomer(customerId, linked),
        storage.getFoldersByCustomer(customerId, linked).catch((err) => {
          // An ABORT must not be degraded to "no folders". `refreshAsync`
          // cancels the previous call and relies on the outer catch seeing the
          // AbortError and returning the existing state untouched; swallowing
          // it here would let a superseded refresh run to completion and
          // publish an empty folder list — and if the projects leg had already
          // fulfilled, `Promise.all` resolves normally so the outer catch never
          // even runs. Every folder would vanish from the grid until the next
          // refresh, and with `m_folders` empty the un-file legs in
          // `deleteAsync` / `moveProjectsToFolderAsync` go quiet and start
          // stranding map rows.
          if ((err as { name?: string })?.name === "AbortError") throw err;
          console.warn("[HttpProjectListModel] folder load failed (continuing):", err);
          return [] as ReadonlyArray<FolderInfo>;
        }),
        storage.getAllSharedLinks(customerId, linked).catch((err) => {
          if ((err as { name?: string })?.name === "AbortError") throw err;
          console.warn("[HttpProjectListModel] shared-link load failed (continuing):", err);
          return [] as ReadonlyArray<SharedLinkDto>;
        }),
      ]);
      const shareState = buildOwnAccountShareState(sharedLinks, auth.currentUser?.email ?? "");
      // AI-1178 parity: tolerate a single malformed/partial row instead of
      // failing the whole grid — the web-idiomatic equivalent of Unity's
      // `CustomerModel.GetProjects` null-guard, which skips a shared link
      // whose project row is missing and keeps the loop going rather than
      // aborting the entire shared-links pass. `mapDtoToInfo` doesn't throw
      // today (every field is null-guarded), so this is defensive; it also
      // readies the seam for the deferred external-shares join, where a
      // link can legitimately point at an absent row. A plain for-loop
      // preserves order (createDraft prepend + view sort unchanged).
      const mapped: ProjectInfo[] = [];
      for (const dto of dtos) {
        // Port of `ProjectsViewModel.CheckDuplication` (`:2599`): a row the
        // server is still assembling belongs to whoever asked for it, and
        // showing a half-built project to everyone else in the account would
        // offer a card that cannot be opened. Our OWN in-flight copy is kept —
        // that is the placeholder the grid is showing progress on.
        if (dto.IsDuplicating === true && !this.isProjectDuplicating(dto.UID)) continue;
        try {
          const info = mapVisibleOwnAccountProject(dto, userId, shareState);
          if (info) mapped.push(info);
        } catch (err) {
          console.warn(
            "[HttpProjectListModel] skipping malformed project row",
            (dto as { UID?: string })?.UID,
            err,
          );
        }
      }
      // Cross-account share join (Unity `CustomerModel.GetSharedProjects`):
      // links shared WITH this user live in the SHARING account's Dynamo
      // partition, so they can only arrive via the admin GraphQL pipeline.
      // Own-account rows are already in `mapped`; merge rules below mirror
      // Unity's. A failing/absent admin API degrades to own-account only.
      this.m_projects = this.withDuplicationPlaceholder(
        await this.mergeExternalSharesAsync(mapped, customerId, linked),
      );
      // Folders are pruned + kept against the OWN-ACCOUNT rows, deliberately:
      // Unity prunes against `allAccountProjects` before the shared-project
      // join (`CustomerModel.cs:1094-1100`), which is sound because
      // `AddProjectsToFolderAsync` refuses to file a project from another
      // account in the first place — so a cross-account project can never be a
      // legitimate member of one of OUR folders.
      this.m_folders = resolveFolders(folderRows, mapped, userId);
      this.m_lastError = null;
      this.emitChanged();
      this.emitFoldersChanged();
      return this.m_projects;
    } catch (err) {
      if ((err as { name?: string })?.name === "AbortError") {
        return this.m_projects;
      }
      this.m_lastError = err instanceof Error ? err : new Error(String(err));
      throw this.m_lastError;
    } finally {
      if (this.m_inflight === controller) this.m_inflight = null;
      this.setLoading(false);
    }
  }

  /**
   * Port of `CustomerModel.GetSharedProjects` (non-hybrid path). Merge rules,
   * verbatim:
   *  - an individual link for a project already visible via ACCOUNT-wide
   *    sharing OVERRIDES the account role — `NoAccess` removes the project
   *    entirely (that's how "leave project" works for enterprise members);
   *  - a standalone link with `NoAccess` is skipped;
   *  - otherwise synthesize the project from the pipeline's own metadata
   *    (name / author / timestamps come from the pipeline, NOT the link row),
   *    carrying `customerId = link.CustomerID` so every later storage call
   *    hits the owning account's partition.
   */
  private async mergeExternalSharesAsync(
    ownProjects: ReadonlyArray<ProjectInfo>,
    ownCustomerId: string,
    signal?: AbortSignal,
  ): Promise<ProjectInfo[]> {
    const merged = [...ownProjects];
    if (!Ioc.isRegistered<HttpAdminStorageModel>(ADMIN_STORAGE_MODEL)) return merged;

    let shares: ReadonlyArray<ExternalProjectShare>;
    try {
      shares = await Ioc.resolve<HttpAdminStorageModel>(ADMIN_STORAGE_MODEL)
        .getExternalProjectSharesAsync(signal);
    } catch (err) {
      console.warn("[HttpProjectListModel] external-share join failed:", err);
      return merged;
    }
    if (shares.length === 0) return merged;

    for (const share of shares) {
      const existingIndex = merged.findIndex((p) => p.id === share.projectId);
      const isNoAccess = share.userRole === SHARED_USER_ROLE.NoAccess;

      if (existingIndex >= 0) {
        // The individual link wins over the account-wide grant.
        if (isNoAccess) {
          merged.splice(existingIndex, 1);
          continue;
        }
        merged[existingIndex] = {
          ...merged[existingIndex],
          customerId: share.customerId || merged[existingIndex].customerId,
          sharedLinkId: share.id,
          sharedUserRole: share.userRole,
          isShared: true,
          category: "shared",
        };
        continue;
      }
      if (isNoAccess) continue; // revoked standalone link

      merged.push({
        id: share.projectId,
        name: share.projectName,
        ownerName: share.projectAuthor,
        customerId: share.customerId || ownCustomerId,
        sharedLinkId: share.id,
        sharedUserRole: share.userRole,
        modifiedAt: share.projectModifiedAt,
        createdAt: share.projectCreatedAt || undefined,
        thumbnailUrls: [],
        isShared: true,
        isUserCreated: true,
        category: "shared",
      });
    }
    return merged;
  }

  getById(projectId: string): ProjectInfo | null {
    return this.m_projects.find((p) => p.id === projectId) ?? null;
  }

  async createDraftAsync(name?: string, folderId?: string | null): Promise<ProjectInfo> {
    const auth = Ioc.resolve<IAuthModel>(AUTH_MODEL);
    const user = auth.currentUser;
    const customerId = user?.customerId;
    if (!customerId || !user) {
      throw new Error(
        "[HttpProjectListModel] createDraftAsync: no customer (sign in first)",
      );
    }

    // Mirrors `CustomerModel.CreateProjectAsync`
    // (`Models/CustomerModel/CustomerModel.cs:1254-1278`):
    //   project.UID = Guid.NewGuid()
    //   project.Name = $"{TemplateNames.GetRandom()} project"
    //   project.Author = CurrentUser.NameTag
    //   project.CreatorId = CurrentUser.UserID
    //   project.AccountRole = SharedUserRole.NoAccess
    //   project.PublishingStatus = Draft (= 0)
    //   project.SupportedDeviceMask = All (Desktop|Tablet|Headset = 7)
    //   project.IsDuplicating = false
    //   project.SupportsInstances = false
    //   project.SerializationVersion = V2 (= 2)
    //   Created/Modified = UtcNow (stamped here so unit tests stay
    //                              deterministic; storage layer does
    //                              not re-stamp).
    const uid = randomUuid();
    const trimmed = name?.trim();
    const projectName = trimmed && trimmed.length > 0
      ? trimmed
      : `${randomTemplateName()} project`;
    const now = new Date().toISOString();

    const dto: ProjectDto = {
      UID: uid,
      Name: projectName,
      Author: user.displayName,
      CreatorId: user.userId,
      CreatedTimestamp: now,
      ModifiedTimestamp: now,
      AccountRole: 4, // SharedUserRole.NoAccess
      PublishingStatus: 0, // PublishingStatus.Draft
      SupportedDeviceMask: 7, // Desktop|Tablet|Headset = All
      IsDuplicating: false,
      SupportsInstances: false,
      SerializationVersion: PROJECT_SERIALIZATION_V2,
    };

    const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
    await storage.createCustomerProject(customerId, dto);

    // Map + prepend so the new card appears at the top of the grid
    // immediately. Unity does the same: `m_projects.Add(projectInfo.ID,
    // projectInfo)` then the view sorts by `ModifiedTimestamp` desc.
    const info = mapDtoToInfo(dto, user.userId);
    this.m_projects = [info, ...this.m_projects];
    this.emitChanged();
    // Unity `CustomerModel.CreateProjectAsync` → `SendCreateProjectEvent`
    // (projectId only; peers refresh their project list).
    this.publishProjectEvent("create_project", {
      projectId: info.id,
      ProjectId: info.id,
    });

    // Filed AFTER the create event, so a peer that refetches on `create_project`
    // sees the project row exist before the membership event tells it where the
    // project lives. Non-fatal: a project that fails to file is still a real
    // project, and it lands at the root where the user can move it by hand.
    if (folderId && this.getFolderById(folderId)) {
      try {
        await this.moveProjectsToFolderAsync([info.id], folderId);
      } catch (err) {
        console.warn(
          `[HttpProjectListModel] could not file new project ${info.id} into ${folderId}:`,
          err,
        );
      }
    }
    return info;
  }

  async renameAsync(projectId: string, name: string): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) return; // Mirrors Unity's `IsNullOrWhiteSpace` reject.
    const current = this.getById(projectId);
    if (!current) {
      throw new Error(`[HttpProjectListModel] renameAsync: unknown project ${projectId}`);
    }
    if (current.name === trimmed) return;

    const auth = Ioc.resolve<IAuthModel>(AUTH_MODEL);
    const customerId = auth.currentUser?.customerId;
    if (!customerId) {
      throw new Error("[HttpProjectListModel] renameAsync: no customer (sign in first)");
    }

    const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
    const { modifiedAt } = await storage.updateProjectName(
      customerId,
      projectId,
      trimmed,
    );

    // Unity `UpdateSharedLinks`: the project name is denormalized onto every
    // shared-link row, so a rename must fan out or sharees keep seeing the
    // old title in their grid.
    try {
      const links = await storage.getSharedLinksByProject(customerId, projectId);
      if (links.length > 0) {
        await storage.updateSharedLinksProjectName(
          customerId,
          links.map((link) => link.ID),
          trimmed,
        );
      }
    } catch (err) {
      console.warn(
        `[HttpProjectListModel] shared-link rename fan-out failed for ${projectId}:`,
        err,
      );
    }

    // Patch the in-memory entry so the grid updates immediately
    // without waiting for `refreshAsync`. Mirrors Unity's
    // `ProjectViewModel.UpdateProjectName` which mutates `Project.Name`
    // before persisting and then lets the multiplayer event roundtrip
    // refresh the rest of the row.
    const modifiedByName = auth.currentUser?.displayName || undefined;
    this.m_projects = this.m_projects.map((p) =>
      p.id === projectId ? { ...p, name: trimmed, modifiedAt, modifiedByName } : p,
    );
    this.emitChanged();

    // ...and the OPEN project, which is a different model holding its own copy
    // of the same `ProjectInfo`. It cannot learn about this from the wire:
    // `projectEventReconciliation` drops own-`aid` echoes, so a rename issued
    // from the editor's own header would broadcast to every peer and leave the
    // header that sent it showing the old title. The inbound leg calls both
    // models (`applyProjectNameChange` + `applyProjectRename`); this is the
    // outbound half of that pair, kept here in the single writer rather than in
    // the caller so every rename entry point gets it. No-ops unless `projectId`
    // is the project currently loaded.
    if (Ioc.isRegistered<IProjectModel>(PROJECT_MODEL)) {
      Ioc.resolve<IProjectModel>(PROJECT_MODEL).applyProjectNameChange(projectId, trimmed);
    }

    // Unity `CustomerModel.UpdateProjectNameAsync` → `SendUpdateProjectName`
    // (`ProjectEventData`, tc 27): peers apply `projectName` to their grid
    // row + active-project header live.
    this.publishProjectEvent("update_project", {
      projectId,
      ProjectId: projectId,
      customerId,
      projectName: trimmed,
    });
  }

  /**
   * Inbound reconcile for a peer's `update_project` rename. Payload-only
   * (`CustomerModel.ApplyProjectUserDisplaySettings` + the header/grid
   * subscribers on the Unity side); never persists.
   */
  applyProjectRename(projectId: string, name: string): void {
    let mutated = false;
    this.m_projects = this.m_projects.map((p) => {
      if (p.id !== projectId || p.name === name) return p;
      mutated = true;
      return { ...p, name };
    });
    if (mutated) this.emitChanged();
  }

  applyProjectConsoleAsset(
    projectId: string,
    consoleAssetId: string | null,
    assetIds: ReadonlyArray<string>,
  ): void {
    let mutated = false;
    const normalizedId = consoleAssetId?.trim() || undefined;
    this.m_projects = this.m_projects.map((project) => {
      if (project.id !== projectId) return project;
      mutated = true;
      return {
        ...project,
        consoleAssetId: normalizedId,
        assetIds: [...assetIds],
      };
    });
    if (mutated) this.emitChanged();
  }

  applyProjectModifiedDate(projectId: string, modifiedAt: string): void {
    // Every caller reports a write THIS client just made, and the storage
    // layer stamped the signed-in user as its editor (AI-1501) — so the card
    // credits them now rather than after the next refetch.
    const modifiedByName = Ioc.isRegistered<IAuthModel>(AUTH_MODEL)
      ? Ioc.resolve<IAuthModel>(AUTH_MODEL).currentUser?.displayName || undefined
      : undefined;
    let mutated = false;
    this.m_projects = this.m_projects.map((p) => {
      if (p.id !== projectId || p.modifiedAt === modifiedAt) return p;
      mutated = true;
      return { ...p, modifiedAt, modifiedByName };
    });
    if (mutated) this.emitChanged();
  }

  /** Stamp identity + send via the session event socket (when wired). */
  private publishProjectEvent(type: string, payload: Record<string, unknown>): void {
    if (!Ioc.isRegistered<IEventModel>(EVENT_MODEL)) return;
    const auth = Ioc.resolve<IAuthModel>(AUTH_MODEL);
    Ioc.resolve<IEventModel>(EVENT_MODEL).send({
      type,
      payload: {
        ...payload,
        uid: auth.currentUser?.userId ?? null,
        aid: getAppInstanceId(),
      },
    });
  }

  get duplication(): ProjectDuplicationState | null {
    const active = this.m_duplication;
    if (!active) return null;
    return {
      sourceProjectId: active.sourceId,
      targetProjectId: active.targetId,
      targetName: active.placeholder.name,
    };
  }

  /**
   * Port of `IProjectDuplicationModel.IsProjectDuplicating` (`:114`) — TRUE for
   * both ends of the operation. The source counts because its own card must
   * stop offering actions while it is being read.
   */
  private isProjectDuplicating(projectId: string): boolean {
    const active = this.m_duplication;
    if (!active) return false;
    return active.sourceId === projectId || active.targetId === projectId;
  }

  /**
   * Port of `ProjectDuplicationModel.DuplicateProject` (`:133`) on its server
   * path. Long-running: the lambda clones the whole project tree and we wait
   * for it, so the returned promise can be live for minutes.
   *
   * The ordering is Unity's, and three parts of it are load-bearing:
   *
   *  - the target id is minted HERE, before the request. The mutation returns
   *    long before the clone exists, so a caller-chosen id is the only thing
   *    that makes the result findable (`:143-146`).
   *  - the placeholder card goes up before the request and comes down after the
   *    final refresh, so the grid never shows a gap where the copy will be.
   *  - `AccountRole` is reset to `NoAccess` afterwards (`:179-180`). The clone
   *    inherits the source's row, so duplicating an account-shared project
   *    would otherwise publish the copy to the whole account.
   *
   * `folderId` is the folder the copy is filed into — Unity passes the OPEN
   * folder, not the source's (`ProjectsViewModel.cs:1035`), which is why the
   * caller supplies it.
   */
  async duplicateAsync(
    projectId: string,
    folderId?: string | null,
  ): Promise<ProjectInfo | null> {
    const source = this.getById(projectId);
    if (!source) {
      throw new Error(`[HttpProjectListModel] duplicateAsync: unknown project ${projectId}`);
    }
    // Unity refuses concurrent duplication in the VIEW (`OnDuplicateProject`
    // checks `IsProcessing` and shows a dialog). Repeated here because the
    // model owns the state that makes it unsafe, and a second caller must not
    // be able to overwrite the placeholder of the first.
    if (this.m_duplication) {
      throw new Error("[HttpProjectListModel] duplicateAsync: a duplication is already running");
    }
    const { customerId, user } = this.requireUser("duplicateAsync");
    if (!Ioc.isRegistered<HttpAdminStorageModel>(ADMIN_STORAGE_MODEL)) {
      throw new Error(
        "[HttpProjectListModel] duplicateAsync: the admin storage model is not registered",
      );
    }
    const admin = Ioc.resolve<HttpAdminStorageModel>(ADMIN_STORAGE_MODEL);
    const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);

    const targetProjectId = randomUuid();
    const targetName = generateNextCopyName(
      source.name,
      this.m_projects.map((p) => p.name),
    );
    const now = new Date().toISOString();
    // Unity's placeholder carries the SOURCE's id with the new name
    // (`RaiseNewProject(source.ID, …)`), which puts two cards with one id in its
    // grid; keyed by the target id here instead, so the placeholder is the row
    // the finished clone replaces and `v-for` keys stay unique.
    const placeholder: ProjectInfo = {
      id: targetProjectId,
      name: targetName,
      ownerName: user.displayName,
      creatorId: user.userId,
      customerId,
      modifiedAt: now,
      createdAt: now,
      thumbnailUrls: [],
      isShared: false,
      isUserCreated: true,
      category: "drafts",
      isDuplicating: true,
    };
    this.m_duplication = { sourceId: source.id, targetId: targetProjectId, placeholder };
    this.m_projects = this.withDuplicationPlaceholder(this.m_projects);
    this.emitChanged();

    try {
      const created = await duplicateProjectOnServerAsync({
        source,
        targetProjectId,
        targetName,
        targetCustomerId: customerId,
        author: user.displayName,
        admin,
        storage,
      });

      try {
        await rekeyDuplicatedWorkflowAsync({
          sourceProjectId: source.id,
          sourceCustomerId: source.customerId ?? customerId,
          targetProjectId,
          targetProjectName: targetName,
          targetCustomerId: customerId,
          storage,
        });
      } catch (err) {
        // The project clone already exists. Keep it available, but do not let
        // an optional workflow post-process make the whole duplication report
        // failure after the server operation succeeded.
        console.warn(
          `[HttpProjectListModel] workflow rekey failed for duplicated project ${targetProjectId}:`,
          err,
        );
      }

      try {
        await backfillDuplicatedMediaAsync({
          source,
          targetProjectId,
          targetCustomerId: customerId,
          storage,
        });
      } catch (err) {
        // Unity's own try/catch (`:262-266`): a copy missing its media cards is
        // still a copy, and failing the whole operation here would leave a real
        // project behind while telling the user it did not work.
        console.warn(
          `[HttpProjectListModel] media backfill failed for duplicated project ${targetProjectId}:`,
          err,
        );
      }

      if (folderId && this.getFolderById(folderId)) {
        try {
          await storage.addProjectsToFolder(customerId, folderId, [targetProjectId]);
        } catch (err) {
          // Same call as `createDraftAsync`'s filing leg and non-fatal for the
          // same reason: the copy exists either way, at the root.
          console.warn(
            `[HttpProjectListModel] could not file duplicated project ${targetProjectId} into ${folderId}:`,
            err,
          );
        }
      }

      try {
        await storage.updateCustomerProjectAccountRole(
          customerId,
          targetProjectId,
          SHARED_USER_ROLE.NoAccess,
        );
      } catch (err) {
        console.warn(
          `[HttpProjectListModel] could not reset account role on duplicated project ${targetProjectId}:`,
          err,
        );
      }

      // Refreshed while the duplication state is still ACTIVE, deliberately:
      // the clone's row can still carry `IsDuplicating` for a moment, and the
      // peer-hide rule above would drop the brand-new project from the grid.
      await this.refreshAsync().catch((err) => {
        console.warn("[HttpProjectListModel] refresh after duplicate failed:", err);
      });
      // Unity `SendCreateProjectEvent(source.ID)` — the SOURCE id, not the
      // copy's. Peers only use the event as "refetch the catalog", so the id is
      // informational; kept verbatim so both clients read the same wire shape.
      this.publishProjectEvent("create_project", {
        projectId: source.id,
        ProjectId: source.id,
      });
      return this.getById(targetProjectId) ?? mapDtoToInfo(created, user.userId);
    } finally {
      // Unity's `finally` comment: "reset these values in any case, otherwise
      // it's not possible to duplicate other projects." The placeholder is
      // dropped by IDENTITY, so a real row that arrived under the same id (the
      // success path) is never mistaken for it.
      this.m_duplication = null;
      this.m_projects = this.m_projects.filter((p) => p !== placeholder);
      this.emitChanged();
    }
  }

  /**
   * Keeps the in-flight copy's card in the list. Applied on every publish of
   * `m_projects` rather than inserted once, because `refreshAsync` REPLACES the
   * array — Unity holds its placeholder outside the fetched set for the same
   * reason (`m_rootItems["duplicate"]`, `:2785`).
   */
  private withDuplicationPlaceholder(projects: ProjectInfo[]): ProjectInfo[] {
    const active = this.m_duplication;
    if (!active) return projects;
    if (projects.some((p) => p.id === active.targetId)) return projects;
    return [active.placeholder, ...projects];
  }

  async deleteAsync(projectId: string): Promise<void> {
    const current = this.getById(projectId);
    if (!current) return; // Already gone — no-op (Unity treats this as success).

    const auth = Ioc.resolve<IAuthModel>(AUTH_MODEL);
    const customerId = auth.currentUser?.customerId;
    if (!customerId) {
      const err = new Error(
        "[HttpProjectListModel] deleteAsync: no customer (sign in first)",
      );
      this.onDeleteProjectFailed.emit({ projectId, error: err });
      throw err;
    }

    this.onBeginDeleteProject.emit({ projectId, name: current.name });
    // Mirrors Unity's `DeleteProject` UX — Unity fakes a 60-step
    // 2 s progress bar (`TimeUtils.EstimateProgress(2000, 60, …)`)
    // because the actual Dynamo round-trip is sub-100ms and the
    // user expects to see *something* tick. We do the same: emit
    // intermediate progress, run the delete, then complete.
    this.onDeleteProjectProgress.emit({ projectId, progress01: 0.1 });

    try {
      const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
      this.onDeleteProjectProgress.emit({ projectId, progress01: 0.4 });
      // Unity's `DeleteCustomerProject` purges the project's shared-link rows
      // BEFORE deleting the project row, so a revoked project can't leave
      // dangling grants in the sharing pipeline — and `CustomerModel.
      // DeleteProjectAsync` first tells every external sharee's account the
      // project is gone (`change_sharing ProjectUnshared`), so their grid drops
      // it live instead of on the next refresh.
      try {
        const links = await storage.getSharedLinksByProject(customerId, projectId);
        if (Ioc.isRegistered<ISharedUsersModel>(SHARED_USERS_MODEL)) {
          await Ioc.resolve<ISharedUsersModel>(SHARED_USERS_MODEL)
            .notifyProjectDeletedAsync(current, links)
            .catch(() => undefined);
        }
        for (const link of links) {
          await storage.deleteSharedLink(customerId, link.ID);
        }
      } catch (err) {
        console.warn(
          `[HttpProjectListModel] shared-link cleanup failed for ${projectId} (continuing):`,
          err,
        );
      }
      // Unity `CustomerModel.DeleteProjectAsync` (`:1591-1595`) un-files the
      // project BEFORE deleting its row, and deliberately sends no
      // `update_folder` event for it (peers learn from `delete_project`).
      //
      // This is not tidiness. A stranded `!folder:{id}:map:{projectId}` row is
      // invisible here — every reader prunes ids it cannot resolve — but the
      // DESKTOP client reads an unresolvable member as "a project belonging to
      // someone else" (`FolderInfo.ContainsProjectsFromOtherUsers`,
      // `FolderInfo.cs:108-114`), which permanently trips its
      // "Cannot delete folder – has projects created by other users." refusal.
      // So a web-side project delete would silently make the folder
      // undeletable on desktop, with nothing on either client to explain it.
      const folder = this.getFolderForProject(projectId);
      if (folder) {
        try {
          await storage.removeProjectsFromFolder(customerId, folder.id, [projectId]);
          this.m_folders = this.m_folders.map((f) =>
            f.id === folder.id
              ? { ...f, projectIds: f.projectIds.filter((id) => id !== projectId) }
              : f,
          );
          this.emitFoldersChanged();
        } catch (err) {
          // Non-fatal, matching the shared-link cleanup above: losing the
          // project is what the user asked for, and the orphan map row is
          // recoverable by re-running the delete.
          console.warn(
            `[HttpProjectListModel] folder un-file failed for ${projectId} (continuing):`,
            err,
          );
        }
      }

      await storage.deleteCustomerProject(customerId, projectId);

      // Drop cached blob URLs for this project — `IProjectThumbnailsCache.
      // ClearThumbnailsForProject` doesn't run from Unity's delete
      // handler either, but we own the cache lifecycle so it's worth
      // releasing the memory now (and forcing a fresh fetch if the
      // user re-creates a project with the same id).
      clearProjectThumbnails(projectId);

      this.m_projects = this.m_projects.filter((p) => p.id !== projectId);
      this.emitChanged();
      this.onDeleteProjectProgress.emit({ projectId, progress01: 1 });
      this.onDeleteProjectCompleted.emit({ projectId });
      // Unity `CustomerModel.DeleteProjectAsync` → `SendDeleteProjectEvent`.
      this.publishProjectEvent("delete_project", {
        projectId,
        ProjectId: projectId,
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      this.onDeleteProjectFailed.emit({ projectId, error });
      throw error;
    }
  }

  // ---- Folders ------------------------------------------------------

  getFolderById(folderId: string): FolderInfo | null {
    return this.m_folders.find((f) => f.id === folderId) ?? null;
  }

  getFolderForProject(projectId: string): FolderInfo | null {
    return this.m_folders.find((f) => f.projectIds.includes(projectId)) ?? null;
  }

  /**
   * Port of `CustomerModel.CreateFolderAsync` (`CustomerModel.cs:1308`),
   * including the default name — `"{TemplateNames.GetRandom()} folder"`, the
   * same generator the project default uses with a different noun.
   */
  async createFolderAsync(isShared = false): Promise<FolderInfo> {
    const { customerId, user } = this.requireUser("createFolderAsync");

    const now = new Date().toISOString();
    const dto: FolderDto = {
      ID: randomUuid(),
      Name: `${randomTemplateName()} folder`,
      CreatorID: user.userId,
      Author: user.displayName,
      IsShared: isShared,
      CreatedTS: now,
      ModifiedTS: now,
    };

    await Ioc.resolve<IStorageModel>(STORAGE_MODEL).createFolder(customerId, dto);

    const info: FolderInfo = {
      id: dto.ID,
      name: dto.Name,
      creatorId: dto.CreatorID,
      author: dto.Author ?? "",
      customerId,
      isSharedFolder: isShared,
      createdAt: now,
      modifiedAt: now,
      projectIds: [],
    };
    this.m_folders = [info, ...this.m_folders];
    this.emitFoldersChanged();
    this.publishFolderEvent(FOLDER_EVENT_TYPE.Create, folderToWire(info));
    return info;
  }

  async renameFolderAsync(folderId: string, name: string): Promise<void> {
    const trimmed = name.trim();
    // Unity rejects a blank rename by restoring the old name in the field
    // (`ProjectsViewModel.UpdateProjectName`, :752-757) rather than writing it.
    if (!trimmed) return;
    const current = this.getFolderById(folderId);
    if (!current || current.name === trimmed) return;

    const { customerId } = this.requireUser("renameFolderAsync");
    await Ioc.resolve<IStorageModel>(STORAGE_MODEL).updateFolderName(
      customerId,
      folderId,
      trimmed,
    );

    this.m_folders = this.m_folders.map((f) =>
      f.id === folderId ? { ...f, name: trimmed } : f,
    );
    this.emitFoldersChanged();
    // Unity's rename event carries the PARTIAL folder `{ID, Name}`
    // (`AWSServerEventSenderModel.cs:278`).
    this.publishFolderEvent(FOLDER_EVENT_TYPE.UpdateName, {
      id: folderId,
      name: trimmed,
    });
  }

  async deleteFolderAsync(folderId: string, deleteProjectsInside: boolean): Promise<void> {
    const folder = this.getFolderById(folderId);
    if (!folder) return;
    const { customerId } = this.requireUser("deleteFolderAsync");

    // Unity `ProjectsViewModel.DeleteFolder` (:1173-1196) deletes the contained
    // projects BEFORE the folder. Order matters on failure: a project delete
    // that throws leaves the folder standing with its remaining contents
    // reachable, whereas dropping the folder first would strand them at the
    // root mid-way through with no record of what was meant to go.
    if (deleteProjectsInside) {
      for (const projectId of folder.projectIds) {
        if (!this.getById(projectId)) continue;
        await this.deleteAsync(projectId);
      }
    }

    await Ioc.resolve<IStorageModel>(STORAGE_MODEL).deleteFolder(customerId, folderId);

    this.m_folders = this.m_folders.filter((f) => f.id !== folderId);
    this.emitFoldersChanged();
    // Delete sends `{ID}` alone (`AWSServerEventSenderModel.cs:304`).
    this.publishFolderEvent(FOLDER_EVENT_TYPE.Delete, { id: folderId });
  }

  async moveProjectsToFolderAsync(
    projectIds: ReadonlyArray<string>,
    targetFolderId: string | null,
  ): Promise<void> {
    if (projectIds.length === 0) return;
    const { customerId } = this.requireUser("moveProjectsToFolderAsync");
    const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);

    const target = targetFolderId ? this.getFolderById(targetFolderId) : null;
    if (targetFolderId && !target) {
      throw new Error(
        `[HttpProjectListModel] moveProjectsToFolderAsync: unknown folder ${targetFolderId}`,
      );
    }

    // Unity's cross-account guard (`CustomerModel.AddProjectsToFolderAsync`,
    // :1340-1348) — it logs and returns for the WHOLE batch on the first
    // offender. We drop only the offending project and move the rest, because
    // the web's move is reachable from a multi-select where one shared project
    // silently cancelling everyone else's move would read as a broken command.
    const movable = projectIds.filter((id) => {
      const project = this.getById(id);
      if (!project) return false;
      const owner = project.customerId ?? customerId;
      if (owner !== customerId) {
        console.warn(
          `[HttpProjectListModel] skipping ${id}: a project owned by ${owner} cannot be filed in this account's folders`,
        );
        return false;
      }
      return true;
    });
    if (movable.length === 0) return;

    // Projects can come from DIFFERENT source folders — Unity acknowledges the
    // same case (`ProjectContextView.AreAllProjectsInTheSameFolder`, :311),
    // which is reachable whenever a search flattens folder children into the
    // grid. So sources are grouped rather than assumed to be one.
    const bySource = new Map<string, string[]>();
    for (const id of movable) {
      const source = this.getFolderForProject(id);
      if (!source || source.id === targetFolderId) continue;
      const list = bySource.get(source.id);
      if (list) list.push(id);
      else bySource.set(source.id, [id]);
    }

    const alreadyThere = new Set(target?.projectIds ?? []);
    const toAdd = movable.filter((id) => !alreadyThere.has(id));

    for (const [sourceId, ids] of bySource) {
      await storage.removeProjectsFromFolder(customerId, sourceId, ids);
    }
    if (targetFolderId && toAdd.length > 0) {
      await storage.addProjectsToFolder(customerId, targetFolderId, toAdd);
    }

    const movedOut = new Set(movable);
    const stamp = new Date().toISOString();
    this.m_folders = this.m_folders.map((folder) => {
      const isSource = bySource.has(folder.id);
      const isTarget = folder.id === targetFolderId;
      if (!isSource && !isTarget) return folder;
      const kept = folder.projectIds.filter((id) => !movedOut.has(id));
      return {
        ...folder,
        // Unity stamps `folder.ModifiedTimestampUtc = DateTime.UtcNow` on both
        // sides of the move (`CustomerModel.cs:1352` / `:1370`) — in memory
        // only, since `UpdateFolderName` is the sole writer of that column.
        modifiedAt: stamp,
        // `movable`, not `toAdd`: `kept` has already stripped EVERY moved id
        // from this list, including any that were already here, so re-adding
        // only the new ones would drop the rest. (`toAdd` is right for the
        // WRITE — there is no row to create for one that is already filed —
        // but wrong for the local list.) Reachable today: a search at the root
        // flattens folder children into the grid beside their folder's card,
        // so a project can be dropped onto the folder it already lives in.
        projectIds: isTarget ? [...kept, ...movable] : kept,
      };
    });
    this.emitFoldersChanged();

    // One event per touched folder, each carrying `{ID, ProjectIDs}` — Unity
    // fires `SendUpdateFolderProjectsEvent` separately from Remove and from Add
    // (`CustomerModel.cs:1356` / `:1373`), so a two-folder move is two events.
    for (const folder of this.m_folders) {
      if (!bySource.has(folder.id) && folder.id !== targetFolderId) continue;
      this.publishFolderEvent(FOLDER_EVENT_TYPE.UpdateProjectList, {
        id: folder.id,
        projectIDs: [...folder.projectIds],
      });
    }
  }

  /**
   * Inbound `update_folder` (tc 57). Unity's receive side
   * (`ProjectsViewModel.OnServerFolder*`, :2823-2853) refetches the whole
   * catalog for Create and for a membership change — it cannot join a peer's
   * project ids against its own catalog otherwise — and applies a rename in
   * place. Same split here, for the same reason.
   */
  applyFolderEventFromCloud(folderEventType: number, wireFolder: unknown): void {
    if (!isWireFolder(wireFolder)) return;
    const wire = wireFolder as WireFolder;
    const folderId = wireFolderId(wire);
    if (!folderId) return;

    switch (folderEventType) {
      case FOLDER_EVENT_TYPE.UpdateName: {
        const current = this.getFolderById(folderId);
        if (!current) return;
        // Name ONLY. A Unity rename payload also carries `projectIDs: []`,
        // `creatorID: ""`, `author: ""` and `isShared: false` — ctor defaults
        // for fields the operation does not own — so anything that merged
        // "whatever is present" would empty the folder and reassign its
        // ownership on every peer rename. See `folderWire.ts`.
        const next = applyWireFolderRename(current, wire);
        if (next === current) return;
        this.m_folders = this.m_folders.map((f) => (f.id === folderId ? next : f));
        this.emitFoldersChanged();
        return;
      }
      case FOLDER_EVENT_TYPE.Delete: {
        if (!this.getFolderById(folderId)) return;
        this.m_folders = this.m_folders.filter((f) => f.id !== folderId);
        this.emitFoldersChanged();
        return;
      }
      case FOLDER_EVENT_TYPE.Create: {
        if (this.getFolderById(folderId)) return;
        // Unity gates this on `IsFolderVisible` and then refetches. We can
        // decide visibility from the payload alone — a brand-new folder has no
        // projects, so the keep-rule reduces to "mine, or account-shared" — and
        // that avoids a full catalog round trip for a folder we would drop.
        const customerId = Ioc.resolve<IAuthModel>(AUTH_MODEL).currentUser?.customerId;
        const userId = Ioc.resolve<IAuthModel>(AUTH_MODEL).currentUser?.userId ?? "";
        if (!customerId) return;
        const created = folderFromWire(wire, customerId);
        if (created.creatorId !== userId && !created.isSharedFolder) return;
        this.m_folders = [created, ...this.m_folders];
        this.emitFoldersChanged();
        return;
      }
      case FOLDER_EVENT_TYPE.UpdateProjectList: {
        // A membership change can make a folder appear or disappear for us (the
        // keep-rule counts resolved projects), and the ids may name projects
        // this client has never fetched — so refetch, exactly like Unity.
        void this.refreshAsync().catch(() => undefined);
        return;
      }
      default:
        return;
    }
  }

  private publishFolderEvent(folderEventType: number, folder: WireFolder): void {
    // All four legs share the `update_folder` type; `folderEvent` is the
    // discriminator. See `models/data/folderWire.ts`.
    this.publishProjectEvent("update_folder", { folderEvent: folderEventType, folder });
  }

  private requireUser(method: string): {
    customerId: string;
    user: NonNullable<IAuthModel["currentUser"]>;
  } {
    const user = Ioc.resolve<IAuthModel>(AUTH_MODEL).currentUser;
    const customerId = user?.customerId;
    if (!user || !customerId) {
      throw new Error(`[HttpProjectListModel] ${method}: no customer (sign in first)`);
    }
    return { customerId, user };
  }

  // -- internals --

  private setLoading(value: boolean): void {
    if (this.m_isLoading === value) return;
    this.m_isLoading = value;
    this.onLoadingChanged.emit(value);
  }

  private emitChanged(): void {
    this.onChanged.emit(this.m_projects);
  }

  private emitFoldersChanged(): void {
    this.onFoldersChanged.emit(this.m_folders);
  }
}

/**
 * Port of `CustomerModel.PruneMissingProjectsInFolder` (`:1154`) +
 * `MapProjectsToFolders` (`:1218`), fused — the web has no separate
 * `FolderInfo.MapProjects` step because it keeps ids rather than resolved
 * `ProjectInfo`s.
 *
 * Two rules, both Unity's:
 *  - **Prune** membership down to project ids that actually exist. Unity does
 *    this in memory only and never writes the pruned list back, so a project
 *    deleted by another client leaves its map row behind forever; matching that
 *    is deliberate, since "repairing" it would have every client that merely
 *    OPENS the grid issue deletes against another account member's folder.
 *  - **Keep** a folder only when it is your own, non-empty after the prune, or
 *    account-shared (`:1231-1236` — "do not show empty folders from other users
 *    unless it is shared within the account").
 */
function resolveFolders(
  rows: ReadonlyArray<FolderInfo>,
  projects: ReadonlyArray<ProjectInfo>,
  currentUserId: string,
): FolderInfo[] {
  const known = new Set(projects.map((p) => p.id));
  const kept: FolderInfo[] = [];
  for (const folder of rows) {
    const projectIds = folder.projectIds.filter((id) => known.has(id));
    const isOwnFolder = folder.creatorId === currentUserId;
    if (!isOwnFolder && projectIds.length === 0 && !folder.isSharedFolder) continue;
    kept.push({ ...folder, projectIds });
  }
  return kept;
}

/**
 * Map a Dynamo `ProjectDto` to the UI-shaped `ProjectInfo`. Mirrors
 * the relevant projections in Unity's `ProjectViewModel` constructor
 * + `ProjectInfo.IsShared` / `IsUserCreatedProject`.
 */
export function mapDtoToInfo(dto: ProjectDto, currentUserId: string): ProjectInfo {
  return mapDtoToInfoWithSharing(dto, currentUserId, {});
}

function mapDtoToInfoWithSharing(
  dto: ProjectDto,
  currentUserId: string,
  sharing: {
    readonly hasSharedLinks?: boolean;
    readonly sharedLink?: SharedLinkDto;
  },
): ProjectInfo {
  const isShared = isSharedProject(dto) || sharing.hasSharedLinks === true || Boolean(sharing.sharedLink);
  const isUserCreated = isUserCreatedProject(dto);
  const isOwn = dto.CreatorId === currentUserId;

  // Replicates Unity's `ProjectsViewModel.HasDraftProjects` /
  // `HasSharedProjects` / sample predicate (see analysis in
  // `docs/work-plan/README.md` → M6).
  let category: ProjectInfo["category"];
  if (!isUserCreated) category = "learn";
  else if (isOwn && !isShared) category = "drafts";
  else category = "shared";

  return {
    id: dto.UID,
    name: dto.Name ?? "",
    ownerName: dto.Author ?? "",
    creatorId: dto.CreatorId,
    modifiedAt: dto.ModifiedTimestamp,
    modifiedByName: lastModifiedByName(dto),
    createdAt: dto.CreatedTimestamp,
    thumbnailUrls: [],
    sceneCount: undefined,
    isShared,
    isUserCreated,
    category,
    accountRole: typeof dto.AccountRole === "number" ? dto.AccountRole : undefined,
    sharedLinkId: sharing.sharedLink?.ID,
    sharedUserRole: sharing.sharedLink ? sharedLinkUserRole(sharing.sharedLink) : undefined,
    serializationVersion:
      typeof dto.SerializationVersion === "number"
        ? dto.SerializationVersion
        : 1,
    assetIds: Array.isArray(dto.AssetIDs) ? dto.AssetIDs : undefined,
    documentStoragePaths: Array.isArray(dto.DocumentStoragePaths)
      ? dto.DocumentStoragePaths
      : undefined,
    hideAvatars: dto.HideAvatars === true,
    hideUserTags: dto.HideUserTags === true,
    consoleAssetId:
      typeof dto.ConsoleAssetID === "string" && dto.ConsoleAssetID.length > 0
        ? dto.ConsoleAssetID
        : undefined,
    // Only OUR in-flight copy can reach this mapper with the flag set — the
    // refresh above drops every other client's — and it has to survive, or a
    // refresh landing mid-duplication (a peer's `create_project`, say) would
    // swap the placeholder for a card that looks finished and cannot be opened.
    isDuplicating: dto.IsDuplicating === true,
  };
}

interface OwnAccountShareState {
  readonly currentUserLinksByProject: ReadonlyMap<string, SharedLinkDto>;
  readonly sharedLinkProjectIds: ReadonlySet<string>;
}

function buildOwnAccountShareState(
  sharedLinks: ReadonlyArray<SharedLinkDto>,
  currentUserEmail: string,
): OwnAccountShareState {
  const normalizedEmail = currentUserEmail.trim().toLowerCase();
  const currentUserLinksByProject = new Map<string, SharedLinkDto>();
  const sharedLinkProjectIds = new Set<string>();

  for (const link of sharedLinks) {
    if (!isRosterShareLink(link) || !link.ProjectID) continue;
    sharedLinkProjectIds.add(link.ProjectID);
  }

  for (const link of sharedLinks) {
    if (!isPersonProjectShareLink(link) || !link.ProjectID) continue;

    const sharedWithEmail = (link.SharedWithEmail ?? "").trim().toLowerCase();
    if (normalizedEmail && sharedWithEmail === normalizedEmail) {
      currentUserLinksByProject.set(link.ProjectID, link);
    }
  }

  return { currentUserLinksByProject, sharedLinkProjectIds };
}

function mapVisibleOwnAccountProject(
  dto: ProjectDto,
  currentUserId: string,
  sharing: OwnAccountShareState,
): ProjectInfo | null {
  const isOwn = dto.CreatorId === currentUserId;
  const isAccountShared = isSharedProject(dto);
  const ownHasSharedLinks = isOwn && sharing.sharedLinkProjectIds.has(dto.UID);
  const currentUserShareLink = sharing.currentUserLinksByProject.get(dto.UID);
  const currentUserRole = currentUserShareLink ? sharedLinkUserRole(currentUserShareLink) : undefined;

  // Unity lets an individual NoAccess link override an account-wide grant
  // (`GetSharedProjects` removes the already-visible project in that case).
  if (!isOwn && currentUserRole === SHARED_USER_ROLE.NoAccess) return null;

  // Unity's account scan admits rows that are either mine, account-wide
  // shared, or explicitly shared with me. A private row owned by another
  // account member is still fetched from Dynamo, but it is not visible in
  // Drafts or Shared (`CustomerModel.GetFoldersAndProjectsAsync`, then
  // `GetSharedProjects`).
  if (!isOwn && !isAccountShared && !currentUserShareLink) return null;

  return mapDtoToInfoWithSharing(dto, currentUserId, {
    hasSharedLinks: ownHasSharedLinks,
    sharedLink: currentUserShareLink,
  });
}

function isPersonProjectShareLink(link: SharedLinkDto): boolean {
  const type = link.LinkType ?? 0;
  if (type !== DEEPLINK_TYPE.ProjectLink && type !== DEEPLINK_TYPE.SceneLink) return false;
  const email = (link.SharedWithEmail ?? "").trim().toLowerCase();
  return Boolean(email) && email !== INTERNAL_SHARE_EMAIL;
}

function linkSignals(a: AbortSignal, b: AbortSignal): AbortSignal {
  if (a.aborted) return a;
  if (b.aborted) return b;
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  a.addEventListener("abort", onAbort, { once: true });
  b.addEventListener("abort", onAbort, { once: true });
  return controller.signal;
}
